import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface AdminLoadingContextType {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  loadingMessage: string;
  setLoadingMessage: (message: string) => void;
}

const AdminLoadingContext = createContext<AdminLoadingContextType | undefined>(undefined);

export const useAdminLoading = () => {
  const context = useContext(AdminLoadingContext);
  if (context === undefined) {
    throw new Error('useAdminLoading must be used within an AdminLoadingProvider');
  }
  return context;
};

interface AdminLoadingProviderProps {
  children: ReactNode;
}

export const AdminLoadingProvider: React.FC<AdminLoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessageState] = useState('Loading...');

  // Memoize setters to prevent infinite re-renders
  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const setLoadingMessage = useCallback((message: string) => {
    setLoadingMessageState(message);
  }, []);

  // Memoize context value
  const value = React.useMemo(() => ({
    isLoading,
    setLoading,
    loadingMessage,
    setLoadingMessage
  }), [isLoading, setLoading, loadingMessage, setLoadingMessage]);

  return (
    <AdminLoadingContext.Provider value={value}>
      {children}
    </AdminLoadingContext.Provider>
  );
};